import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Dropdown, Row, Col } from 'react-bootstrap';
import { Megaphone } from 'lucide-react';

import { ThemeContext } from "../../../context/ThemeContext";
import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import FeedbackWidget from '../Common/FeedbackWidget';

// Dashboard sub-components
import MetricCard from './Dashboard/MetricCard';
import ReceivablesWidget from './Dashboard/ReceivablesWidget';
import BankAccountsSection from './Dashboard/BankAccountsSection';
import BurnRateIndicator from './Dashboard/BurnRateIndicator';
import RevenueChart from './Dashboard/RevenueChart';
import ExpenseChart from './Dashboard/ExpenseChart';
// import QuickActions from './Dashboard/QuickActions';  // Hidden - actions available in navigation
// import CashFlowChart from './Dashboard/CashFlowChart';  // Hidden - for simpler overview

import OnboardingChecklist from '../Onboarding/OnboardingChecklist';

const Home = () => {
    const { companyId } = useParams();
    const history = useHistory();
    const { changeBackground } = useContext(ThemeContext);
    const [loading, setLoading] = useState(true);
    const [onboardingChecked, setOnboardingChecked] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        overview: {
            totalRevenue: 0,
            totalExpenses: 0,
            netProfit: 0,
            cashFlow: 0,
            previousPeriod: null,
            changes: null,
            metadata: null
        },
        invoices: {
            sent: 0,
            paid: 0,
            unpaid: 0,
            overdue: 0,
            totalOutstanding: 0
        },
        bankAccounts: {
            accounts: [],
            totalBalance: 0
        },
        period: null
    });

    const [selectedPeriod, setSelectedPeriod] = useState('last_12_months');
    const [showFeedback, setShowFeedback] = useState(false);
    // const [cashFlowPeriod, setCashFlowPeriod] = useState('last_12_months');  // Hidden chart
    // const [cashFlowAnalysis, setCashFlowAnalysis] = useState('balance');  // Hidden chart

    useEffect(() => {
        changeBackground({ value: "light", label: "Light" });
    }, []);

    // Check onboarding status — redirect to wizard if first-time user
    useEffect(() => {
        let cancelled = false;

        const checkOnboarding = async () => {
            if (!companyId) {
                setOnboardingChecked(true);
                return;
            }
            try {
                const url = getApiUrl(`${companyId}/onboarding/status`);
                const response = await axiosInstance.get(url);
                const status = response.data;
                if (!cancelled && !status.wizardCompleted && !status.steps.fiscalYear.completed) {
                    history.replace(`/${companyId}/onboarding`);
                    return;
                }
            } catch (error) {
                // If onboarding check fails, don't block the dashboard
                console.error('Onboarding check failed:', error);
            }
            if (!cancelled) setOnboardingChecked(true);
        };

        checkOnboarding();
        return () => { cancelled = true; };
    }, [companyId]);

    useEffect(() => {
        let cancelled = false;

        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const period = selectedPeriod || 'current_fiscal_year';
                const url = getApiUrl(`${companyId}/dashboard/overview?period=${period}`);

                const response = await axiosInstance.get(url);

                if (!cancelled && response.status === 200) {
                    setDashboardData(response.data);
                }
            } catch (error) {
                if (!cancelled) console.error('Error fetching dashboard data:', error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchDashboardData();

        return () => { cancelled = true; };
    }, [companyId, selectedPeriod]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatPeriodLabel = () => {
        const period = dashboardData.period;
        if (!period || !period.startDate || !period.endDate) return null;

        const formatDate = (dateStr) => {
            const d = new Date(dateStr);
            return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
        };

        return `${formatDate(period.startDate)} – ${formatDate(period.endDate)}`;
    };

    const getComparisonPeriodLabel = () => {
        switch (selectedPeriod) {
            case 'this_month':
                return 'Föregående månad';
            case 'last_3_months':
                return 'Föregående 3 månader';
            case 'last_6_months':
                return 'Föregående 6 månader';
            case 'last_12_months':
                return 'Föregående 12 månader';
            case 'current_fiscal_year':
                return 'Föregående räkenskapsår';
            case 'last_year':
                return 'Året innan';
            default:
                return 'Föregående period';
        }
    };

    if (loading || !onboardingChecked) {
        return (
            <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                <div className="dashboard-loading">
                    <div className="dashboard-loading__spinner">
                        <div className="dashboard-loading__circle"></div>
                        <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Laddar dashboard...</span>
                    </div>
                </div>
            </div>
        );
    }

    const { overview } = dashboardData;
    const periodLabel = formatPeriodLabel();
    const accountBreakdown = overview.accountBreakdown || {};

    // Info configurations for metric cards - using actual account data
    const metricInfoConfigs = {
        revenue: {
            title: 'Intäkter',
            accounts: accountBreakdown.revenueAccounts || [],
            reportLink: `/${companyId}/reports/result`,
            totalLabel: 'Totala intäkter'
        },
        expenses: {
            title: 'Kostnader',
            accounts: accountBreakdown.expenseAccounts || [],
            reportLink: `/${companyId}/reports/result`,
            totalLabel: 'Totala kostnader'
        },
        netProfit: {
            title: 'Nettovinst',
            // Combine revenue (positive) and expenses (negative) for net profit view
            accounts: [
                ...(accountBreakdown.revenueAccounts || []).slice(0, 3).map(a => ({ ...a, amount: a.amount })),
                ...(accountBreakdown.expenseAccounts || []).slice(0, 3).map(a => ({ ...a, amount: -a.amount }))
            ],
            reportLink: `/${companyId}/reports/result`,
            totalLabel: 'Nettovinst'
        },
        cashFlow: {
            title: 'Kassaflöde',
            // For cash flow, show bank accounts from bankAccounts data
            accounts: dashboardData.bankAccounts?.accounts?.map(acc => ({
                accountNumber: acc.accountNumber,
                accountName: acc.accountName,
                amount: acc.balance
            })) || [],
            reportLink: `/${companyId}/reports/balance`,
            totalLabel: 'Totalt kassaflöde'
        }
    };

    return (
        <div>
            {/* Onboarding checklist */}
            <OnboardingChecklist companyId={companyId} />

            {/* Period selector */}
            <div className="dashboard-header d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
                    {periodLabel && (
                        <div className="dashboard-period-indicator">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--secondary)' }}>
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                                <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <span>{periodLabel}</span>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowFeedback(true)}
                        className="dashboard-feedback-link"
                    >
                        <Megaphone size={13} strokeWidth={1.75} />
                        Feedback
                    </button>
                </div>
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="dropdown-toggle"
                        style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: '#374151',
                            cursor: 'pointer'
                        }}
                    >
                        {selectedPeriod === 'current_fiscal_year' ? 'Räkenskapsår' :
                            selectedPeriod === 'this_month' ? 'Denna månad' :
                            selectedPeriod === 'last_3_months' ? 'Senaste 3 mån' :
                            selectedPeriod === 'last_6_months' ? 'Senaste 6 mån' :
                            selectedPeriod === 'last_12_months' ? 'Senaste 12 mån' :
                            selectedPeriod === 'last_year' ? 'Förra året' :
                            'Period'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item active={selectedPeriod === 'this_month'} onClick={() => setSelectedPeriod('this_month')}>Denna månad</Dropdown.Item>
                        <Dropdown.Item active={selectedPeriod === 'last_3_months'} onClick={() => setSelectedPeriod('last_3_months')}>Senaste 3 månaderna</Dropdown.Item>
                        <Dropdown.Item active={selectedPeriod === 'last_6_months'} onClick={() => setSelectedPeriod('last_6_months')}>Senaste 6 månaderna</Dropdown.Item>
                        <Dropdown.Item active={selectedPeriod === 'last_12_months'} onClick={() => setSelectedPeriod('last_12_months')}>Senaste 12 månaderna</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item active={selectedPeriod === 'current_fiscal_year'} onClick={() => setSelectedPeriod('current_fiscal_year')}>Räkenskapsår</Dropdown.Item>
                        <Dropdown.Item active={selectedPeriod === 'last_year'} onClick={() => setSelectedPeriod('last_year')}>Förra året</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <FeedbackWidget
                show={showFeedback}
                onHide={() => setShowFeedback(false)}
                source="dashboard"
            />

            {/* Metric Cards - 4 cards in one row */}
            <Row className="mb-4">
                <Col xl={3} lg={6} sm={6} className="mb-3">
                    <MetricCard
                        label="Intäkter"
                        value={formatCurrency(overview.totalRevenue)}
                        colorType="primary"
                        icon={
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--primary)' }}>
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                        change={overview.changes?.totalRevenue}
                        infoConfig={metricInfoConfigs.revenue}
                        period={dashboardData.period}
                    />
                </Col>

                <Col xl={3} lg={6} sm={6} className="mb-3">
                    <MetricCard
                        label="Kostnader"
                        value={formatCurrency(overview.totalExpenses)}
                        colorType="danger"
                        icon={
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#f72b50' }}>
                                <path d="M17 14L12 19L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                        change={overview.changes?.totalExpenses}
                        infoConfig={metricInfoConfigs.expenses}
                        period={dashboardData.period}
                    />
                </Col>

                <Col xl={3} lg={6} sm={6} className="mb-3">
                    <MetricCard
                        label="Nettovinst"
                        value={formatCurrency(overview.netProfit)}
                        colorType="primary"
                        icon={
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--primary)' }}>
                                <path d="M16 21V5C16 4.44772 15.5523 4 15 4H9C8.44772 4 8 4.44772 8 5V21L12 17L16 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                        change={overview.changes?.netProfit}
                        infoConfig={metricInfoConfigs.netProfit}
                        period={dashboardData.period}
                    />
                </Col>

                <Col xl={3} lg={6} sm={6} className="mb-3">
                    <MetricCard
                        label="Kassaflöde"
                        value={formatCurrency(overview.cashFlow)}
                        colorType="info"
                        icon={
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#D653C1' }}>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                        infoConfig={metricInfoConfigs.cashFlow}
                        period={dashboardData.period}
                    />
                </Col>
            </Row>

            {/* Receivables Widget */}
            <Row className="mb-4">
                <Col xl={12}>
                    <ReceivablesWidget />
                </Col>
            </Row>

            {/* Charts Row */}
            <Row className="mb-4">
                <Col xl={8} className="mb-3 mb-xl-0">
                    <div className="dashboard-chart-container">
                        <div className="dashboard-chart-header">
                            <h5 className="dashboard-chart-title">Intäkter vs Kostnader</h5>
                        </div>
                        <RevenueChart />
                    </div>
                </Col>

                <Col xl={4}>
                    <div className="dashboard-chart-container">
                        <div className="dashboard-chart-header">
                            <h5 className="dashboard-chart-title">Kostnadsfördelning</h5>
                        </div>
                        <ExpenseChart />
                    </div>
                </Col>
            </Row>

            {/* Bank Accounts & Burn Rate Row */}
            <Row className="mb-4" style={{ alignItems: 'stretch' }}>
                <Col xl={8} className="mb-3 mb-xl-0 d-flex">
                    <BankAccountsSection
                        bankAccounts={dashboardData.bankAccounts}
                        companyId={companyId}
                        formatCurrency={formatCurrency}
                        style={{ flex: 1 }}
                    />
                </Col>

                <Col xl={4} className="d-flex">
                    <div className="dashboard-section" style={{ position: 'relative', flex: 1 }}>
                        <div className="d-flex align-items-center mb-4">
                            <div className="dashboard-section-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--primary)' }}>
                                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h5 className="mb-0 fw-semibold dashboard-section-title">Kassareserv & Löptid</h5>
                        </div>
                        <BurnRateIndicator />
                    </div>
                </Col>
            </Row>

{/* Cash Flow Chart Row - Hidden for simpler overview
            <Row className="mb-4">
                <Col xl={12}>
                    <div className="dashboard-chart-container">
                        <div className="dashboard-chart-header">
                            <h5 className="dashboard-chart-title">Kassaflöde över tid</h5>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                                        {cashFlowPeriod === 'last_3_months' ? 'Senaste 3 mån' :
                                            cashFlowPeriod === 'last_6_months' ? 'Senaste 6 mån' :
                                                cashFlowPeriod === 'last_12_months' ? 'Senaste 12 mån' :
                                                    cashFlowPeriod === 'last_24_months' ? 'Senaste 24 mån' :
                                                        cashFlowPeriod === 'last_90_days' ? 'Senaste 90 dagar' :
                                                            cashFlowPeriod === 'last_30_days' ? 'Senaste 30 dagar' :
                                                                'Detta år'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_30_days')}>Senaste 30 dagarna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_90_days')}>Senaste 90 dagarna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_3_months')}>Senaste 3 månaderna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_6_months')}>Senaste 6 månaderna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_12_months')}>Senaste 12 månaderna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_24_months')}>Senaste 24 månaderna</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('this_year')}>Detta år</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowPeriod('last_year')}>Förra året</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        {cashFlowAnalysis === 'balance' ? 'Kassasaldo' :
                                            cashFlowAnalysis === 'flows' ? 'Kassaflödesanalys' :
                                                'In- & Utflöde'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => setCashFlowAnalysis('balance')}>Kassasaldo</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowAnalysis('flows')}>Kassaflödesanalys</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setCashFlowAnalysis('inflow_outflow')}>In- & Utflöde</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                        <CashFlowChart period={cashFlowPeriod} analysis={cashFlowAnalysis} />
                    </div>
                </Col>
            </Row>
            */}
        </div>
    );
};

export default Home;
